/**
 * Merchant Product Categories — parent → subcategory hierarchy.
 * Mirrors AdminCategories but scoped to the merchant's shops only.
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { Field, Modal } from "../admin/AdminRestaurants";
import { toast } from "sonner";
import ImageUploader from "../../components/ImageUploader";

const EMPTY = { name: "", image_url: "", parent_id: null, gst_percent: "" };

export default function MerchantCategories() {
    const [rests, setRests] = useState([]);
    const [rid, setRid] = useState("");
    const [rows, setRows] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/merchant/restaurants").then(({ data }) => {
            setRests(data); if (data?.[0]) setRid(data[0].id);
        });
    }, []);
    const load = () => rid && api.get(`/merchant/categories?restaurant_id=${rid}`).then(({ data }) => setRows(data));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [rid]);

    const parents = useMemo(() => rows.filter((r) => !r.parent_id), [rows]);
    const subsBy = useMemo(() => {
        const m = {};
        for (const r of rows) if (r.parent_id) (m[r.parent_id] = m[r.parent_id] || []).push(r);
        return m;
    }, [rows]);

    const openCreate = (parent_id = null) => { setForm({ ...EMPTY, parent_id }); setModal({ mode: "create", parent_id }); };
    const openEdit = (c) => { setForm({ name: c.name, image_url: c.image_url || "", parent_id: c.parent_id || null, gst_percent: c.gst_percent ?? "" }); setModal({ mode: "edit", row: c }); };

    const save = async () => {
        if (!form.name.trim()) return;
        setBusy(true);
        try {
            const gst = form.gst_percent === "" ? null : Number(form.gst_percent);
            if (modal.mode === "edit") {
                await api.patch(`/merchant/categories/${modal.row.id}`, {
                    name: form.name,
                    image_url: form.parent_id ? null : form.image_url,
                    gst_percent: gst,
                });
            } else {
                await api.post(`/merchant/categories`, {
                    restaurant_id: rid,
                    name: form.name,
                    image_url: form.parent_id ? null : form.image_url,
                    priority: rows.length,
                    parent_id: form.parent_id || null,
                    gst_percent: gst,
                });
            }
            toast.success("Saved"); setModal(null); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };

    const remove = async (c) => {
        const label = c.parent_id ? "subcategory" : "category";
        if (!confirm(`Delete this ${label}?`)) return;
        await api.delete(`/merchant/categories/${c.id}`); toast.success("Removed"); load();
    };

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Product Categories</h1>
                    <p className="text-sm text-muted-foreground mt-1">{parents.length} categories · {rows.length - parents.length} subcategories — they appear under each category on your shop page.</p>
                </div>
                <div className="flex items-center gap-2">
                    <select data-testid="mcat-rest-picker" value={rid} onChange={(e) => setRid(e.target.value)} className="bg-card border border-border rounded-xl px-3 py-2 text-sm font-semibold">
                        {rests.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    <button data-testid="mcat-add" onClick={() => openCreate(null)} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><Plus size={16} /> Add</button>
                </div>
            </div>
            <div className="mt-6 bg-card border border-border rounded-2xl divide-y divide-border">
                {parents.map((p) => {
                    const subs = subsBy[p.id] || [];
                    const open = !!expanded[p.id];
                    return (
                        <div key={p.id} data-testid={`mcat-row-${p.id}`}>
                            <div className="flex items-center gap-3 p-4">
                                <button onClick={() => setExpanded((s) => ({ ...s, [p.id]: !open }))} className="p-1 -ml-1 text-muted-foreground">
                                    {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                </button>
                                <div className="w-12 h-12 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                                    {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold truncate">{p.name}</p>
                                    <p className="text-xs text-muted-foreground">{subs.length} subcategor{subs.length === 1 ? "y" : "ies"}</p>
                                </div>
                                <button data-testid={`mcat-add-sub-${p.id}`} onClick={() => openCreate(p.id)} className="text-xs font-semibold text-primary px-3 py-1.5 rounded-lg hover:bg-primary/10 inline-flex items-center gap-1"><Plus size={13} /> Subcategory</button>
                                <button onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-muted"><Pencil size={16} /></button>
                                <button onClick={() => remove(p)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 size={16} /></button>
                            </div>
                            {open && subs.length > 0 && (
                                <div className="bg-muted/30 px-4 py-2 pl-16 space-y-1">
                                    {subs.map((s) => (
                                        <div key={s.id} className="flex items-center gap-2 py-1.5" data-testid={`mcat-sub-${s.id}`}>
                                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                                            <span className="text-sm font-medium flex-1">{s.name}</span>
                                            <button onClick={() => openEdit(s)} className="p-1.5 rounded-lg hover:bg-card"><Pencil size={13} /></button>
                                            <button onClick={() => remove(s)} className="p-1.5 rounded-lg text-destructive hover:bg-destructive/10"><Trash2 size={13} /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
                {parents.length === 0 && <div className="p-8 text-sm text-muted-foreground text-center">No categories yet.</div>}
            </div>
            {modal && (
                <Modal onClose={() => setModal(null)} title={
                    modal.mode === "edit"
                        ? (form.parent_id ? "Edit subcategory" : "Edit category")
                        : (form.parent_id ? "New subcategory" : "New category")
                }>
                    <div className="space-y-3">
                        <Field full label="Name"><input data-testid="mcat-name" autoFocus className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        {!form.parent_id && (
                            <Field full label="Category image">
                                <ImageUploader
                                    value={form.image_url}
                                    onUploaded={(url) => setForm({ ...form, image_url: url })}
                                    aspect="aspect-square"
                                    testId="mcat-image"
                                    className="max-w-[200px]"
                                />
                            </Field>
                        )}
                        <Field full label="GST % (optional)">
                            <input
                                data-testid="mcat-gst-percent"
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                className="inp"
                                value={form.gst_percent}
                                onChange={(e) => setForm({ ...form, gst_percent: e.target.value })}
                                placeholder="Inherits from shop if blank"
                            />
                            <span className="text-[11px] text-muted-foreground mt-1 block">
                                Leave blank to inherit the shop&apos;s GST rate. Enter <code>0</code> for tax-free.
                            </span>
                        </Field>
                        <button data-testid="mcat-save" disabled={busy || !form.name.trim()} onClick={save} className="w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">{busy && <Loader2 size={16} className="animate-spin" />} Save</button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
